import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, Check, Link2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { Badge, EmptyState, fmtMoney, inputCls, MONTH_LABEL } from "./shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FEE_STATUSES = ["paid", "pending", "partially_paid", "failed", "refunded", "overdue"];

export const RecordPaymentDialog = ({ payment, onClose, onSaved }) => {
    const [form, setForm] = useState({ method: "bank_transfer", transaction_id: "", amount: "", payment_date: new Date().toISOString().slice(0, 10), status: "paid", notes: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (payment) setForm({ method: "bank_transfer", transaction_id: "", amount: payment.amount, payment_date: new Date().toISOString().slice(0, 10), status: "paid", notes: "" });
    }, [payment]);

    if (!payment) return null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/payments/${payment.id}/record`, { ...form, amount: form.amount === "" ? null : Number(form.amount) });
            toast.success("Payment recorded");
            onSaved();
        } catch (err) {
            toast.error(fmtApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="record-payment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Record Payment</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">{payment.student_name} · {payment.month_label} · {fmtMoney(payment.amount, payment.currency)}</p>
                <form onSubmit={submit} className="mt-4 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Payment method</Label>
                            <Select value={form.method} onValueChange={set("method")}>
                                <SelectTrigger className={inputCls} data-testid="record-method-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[["bank_transfer", "Bank transfer"], ["upi", "UPI"], ["cash", "Cash"], ["razorpay", "Razorpay"], ["other", "Other"]].map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={set("status")}>
                                <SelectTrigger className={inputCls} data-testid="record-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {FEE_STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Amount received</Label>
                            <Input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} className={inputCls} data-testid="record-amount-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Payment date</Label>
                            <Input type="date" value={form.payment_date} onChange={set("payment_date")} className={inputCls} data-testid="record-date-input" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Transaction / reference ID (optional)</Label>
                        <Input value={form.transaction_id} onChange={set("transaction_id")} className={inputCls} placeholder="UTR / UPI ref / receipt no." data-testid="record-txn-input" />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm font-medium">Cancel</button>
                        <button type="submit" disabled={saving} className="rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark disabled:opacity-60" data-testid="record-save-button">
                            {saving ? "Saving…" : "Save Payment"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default function AdminPayments() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [month, setMonth] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [genMonth, setGenMonth] = useState(new Date().toISOString().slice(0, 7));
    const [recordFor, setRecordFor] = useState(null);
    const [generating, setGenerating] = useState(false);

    const load = () =>
        api.get("/payments", { params: { q, month, status, page } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load payments"));
    useEffect(() => { load(); }, [q, month, status, page]);

    const generate = async () => {
        if (!window.confirm(`Create pending fee entries for ${MONTH_LABEL(genMonth)} for every active student? Existing entries are never duplicated.`)) return;
        setGenerating(true);
        try {
            const r = await api.post("/payments/generate", { month: genMonth });
            toast.success(`Created ${r.data.created} fee record(s) for ${r.data.month_label}${r.data.skipped ? ` (${r.data.skipped} already existed)` : ""}`);
            setMonth(genMonth);
            load();
        } catch (e) {
            toast.error(fmtApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    const paymentLink = async (pid) => {
        try {
            const r = await api.post(`/payments/${pid}/payment-link`);
            await navigator.clipboard.writeText(r.data.url).catch(() => {});
            toast.success("Payment link created and copied — paste it in WhatsApp");
            load();
        } catch (e) {
            toast.error(fmtApiError(e));
        }
    };

    const remind = async (pid) => {
        try {
            const r = await api.post(`/payments/${pid}/reminder`);
            if (!r.data.phone) return toast.error("No phone number on file for this student");
            window.open(r.data.url, "_blank");
        } catch {
            toast.error("Could not create reminder");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-payments">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-semibold text-ink">Payments</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Monthly fee tracking for every student</p>
                </div>
                <div className="flex items-end gap-3 rounded-2xl border border-border bg-white p-4 card-shadow">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Month</Label>
                        <Input type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} className={inputCls} data-testid="generate-month-input" />
                    </div>
                    <button onClick={generate} disabled={generating} data-testid="generate-fees-button"
                        className="flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-teal disabled:opacity-60">
                        <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : "Generate Monthly Fees"}
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-60">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search student…" className={`${inputCls} pl-11`} data-testid="payments-search-input" />
                </div>
                <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPage(1); }} className={`${inputCls} w-44`} data-testid="payments-month-filter" />
                <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger className={`${inputCls} w-48`} data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {FEE_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!data || data.items.length === 0 ? (
                <EmptyState message="No payment records found. Use “Generate Monthly Fees” to create this month's fee entries for all active students." />
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-white card-shadow">
                    <table className="w-full text-sm" data-testid="payments-table">
                        <thead>
                            <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <th className="px-5 py-4 font-medium">Student</th>
                                <th className="px-5 py-4 font-medium">Month</th>
                                <th className="px-5 py-4 font-medium">Amount</th>
                                <th className="px-5 py-4 font-medium">Status</th>
                                <th className="px-5 py-4 font-medium">Method / Txn</th>
                                <th className="px-5 py-4 font-medium">Paid on</th>
                                <th className="px-5 py-4 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((p) => (
                                <tr key={p.id} className="border-b border-border/60 last:border-0" data-testid={`payment-row-${p.id}`}>
                                    <td className="px-5 py-4">
                                        <Link to={`/admin/students/${p.student_id}`} className="font-medium text-ink hover:text-saffron">{p.student_name}</Link>
                                    </td>
                                    <td className="px-5 py-4">{p.month_label}</td>
                                    <td className="px-5 py-4 font-medium">{fmtMoney(p.amount, p.currency)}</td>
                                    <td className="px-5 py-4"><Badge value={p.display_status} /></td>
                                    <td className="max-w-44 px-5 py-4 text-xs text-muted-foreground">
                                        {p.method || p.gateway || "—"}{p.transaction_id ? ` · ${p.transaction_id}` : ""}
                                        {p.payment_link && p.display_status !== "paid" && (
                                            <a href={p.payment_link} target="_blank" rel="noreferrer" className="block truncate text-teal underline">{p.payment_link}</a>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-xs text-muted-foreground">{p.payment_date || "—"}</td>
                                    <td className="px-5 py-4">
                                        {p.display_status !== "paid" ? (
                                            <div className="flex flex-wrap gap-2">
                                                <button onClick={() => setRecordFor(p)} data-testid={`record-payment-${p.id}`}
                                                    className="flex items-center gap-1 rounded-full bg-teal px-3 py-1.5 text-xs font-medium text-cream hover:bg-teal-dark">
                                                    <Check className="h-3 w-3" /> Record
                                                </button>
                                                <button onClick={() => paymentLink(p.id)} data-testid={`payment-link-${p.id}`}
                                                    className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                                    <Link2 className="h-3 w-3" /> Link
                                                </button>
                                                <button onClick={() => remind(p.id)} data-testid={`remind-${p.id}`}
                                                    className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                                    <MessageCircle className="h-3 w-3" /> Remind
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs font-medium text-teal">Settled ✓</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40">Previous</button>
                    <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
                    <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40">Next</button>
                </div>
            )}

            <RecordPaymentDialog payment={recordFor} onClose={() => setRecordFor(null)} onSaved={() => { setRecordFor(null); load(); }} />
        </div>
    );
}
